import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getPersonalizedRecommendations } from '../../../services/openaiService';

const AiRecommendations = ({ userProfile }) => {
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastQuery, setLastQuery] = useState('');

  const generateRecommendations = async (query = 'Donnez-moi des recommandations d\'orientation personnalisées') => {
    if (!userProfile || Object.keys(userProfile)?.length === 0) {
      setError('Veuillez compléter votre profil pour obtenir des recommandations personnalisées.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setLastQuery(query);

    try {
      const aiRecommendations = await getPersonalizedRecommendations(userProfile, query);
      setRecommendations(aiRecommendations);
    } catch (err) {
      console.error('Error generating AI recommendations:', err);
      setError('Impossible de générer les recommandations pour le moment. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile && Object.keys(userProfile)?.length > 0) {
      generateRecommendations();
    }
  }, [userProfile]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return 'AlertTriangle';
      case 'medium':
        return 'Clock';
      case 'low':
        return 'Info';
      default:
        return 'Star';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Icon name="Brain" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Recommandations IA Personnalisées
            </h3>
            <p className="text-sm text-muted-foreground">
              Alimenté par OpenAI GPT-5
            </p>
          </div>
        </div>
        
        <Button
          onClick={() => generateRecommendations(lastQuery)}
          disabled={isLoading}
          variant="outline"
          size="sm"
        >
          {isLoading ? (
            <Icon name="Loader2" size={16} className="animate-spin" />
          ) : (
            <Icon name="RefreshCw" size={16} />
          )}
          {isLoading ? 'Génération...' : 'Actualiser'}
        </Button>
      </div>
      {/* Error State */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center gap-2 text-destructive">
            <Icon name="AlertCircle" size={16} />
            <span className="font-medium">Erreur</span>
          </div>
          <p className="text-sm text-destructive mt-1">{error}</p>
        </div>
      )}
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <Icon name="Brain" size={48} className="text-muted-foreground animate-pulse mb-4 mx-auto" />
            <p className="text-muted-foreground">L'IA analyse votre profil...</p>
            <div className="flex items-center justify-center gap-1 mt-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}
      {/* Recommendations */}
      {recommendations && !isLoading && (
        <div className="space-y-6">
          {/* Summary */}
          {recommendations?.summary && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Lightbulb" size={20} className="text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium text-foreground mb-2">Résumé de l'analyse</h4>
                  <p className="text-sm text-muted-foreground">{recommendations?.summary}</p>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations List */}
          <div className="space-y-4">
            <h4 className="font-medium text-foreground flex items-center gap-2">
              <Icon name="Target" size={16} />
              Recommandations personnalisées
            </h4>
            
            {recommendations?.recommendations?.map((rec, index) => (
              <div key={index} className="border border-border rounded-lg p-4 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-full border ${getPriorityColor(rec?.priority)}`}>
                    <Icon name={getPriorityIcon(rec?.priority)} size={16} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="font-medium text-foreground">{rec?.title}</h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(rec?.priority)}`}>
                        {rec?.priority === 'high' ? 'Haute' : rec?.priority === 'medium' ? 'Moyenne' : 'Faible'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {rec?.description}
                    </p>
                    
                    {rec?.category && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name="Tag" size={12} />
                        <span>{rec?.category}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Next Steps */}
          {recommendations?.next_steps && recommendations?.next_steps?.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-foreground flex items-center gap-2">
                <Icon name="ArrowRight" size={16} />
                Prochaines étapes recommandées
              </h4>
              
              <div className="space-y-2">
                {recommendations?.next_steps?.map((step, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <span className="text-sm text-foreground">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-dashed border-muted-foreground/30">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Zap" size={12} />
              <span>Recommandations générées par OpenAI</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Dernière mise à jour: {new Date()?.toLocaleDateString('fr-FR')}
            </div>
          </div>
        </div>
      )}
      {/* Empty State */}
      {!recommendations && !isLoading && !error && (
        <div className="text-center p-8">
          <Icon name="Brain" size={48} className="text-muted-foreground mb-4 mx-auto" />
          <h4 className="font-medium text-foreground mb-2">Obtenez des recommandations IA</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Complétez votre profil pour recevoir des conseils personnalisés d'orientation
          </p>
          <Button onClick={() => generateRecommendations()}>
            <Icon name="Sparkles" size={16} />
            Générer mes recommandations
          </Button>
        </div>
      )}
    </div>
  );
};

export default AiRecommendations;